import React, { useState, useEffect, useCallback } from 'react';
import { 
  CheckCircle, XCircle, Clock, DollarSign, AlertCircle, 
  ChevronDown, ChevronUp, RefreshCw,
  CreditCard, Banknote, Check,
  User, Mail, Calendar, FileText
} from 'lucide-react';

const PaymentReconciliation = ({ onPendingCountChange }) => {
  const [pendingPayments, setPendingPayments] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('Pending');
  const [expandedId, setExpandedId] = useState(null);
  const [processing, setProcessing] = useState({});
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [showRejectModal, setShowRejectModal] = useState(null);
  const [rejectReason, setRejectReason] = useState('');

  // API base URL
  const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

  const fetchPendingPayments = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      const [paymentsRes, summaryRes] = await Promise.all([
        fetch(`${API_BASE_URL}/PendingPayments?status=${filter}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_BASE_URL}/PendingPayments/summary`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);

      if (!paymentsRes.ok) throw new Error('Failed to load pending payments');
      if (!summaryRes.ok) throw new Error('Failed to load summary');

      const paymentsData = await paymentsRes.json();
      const summaryData = await summaryRes.json();

      setPendingPayments(paymentsData.items || []);
      setSummary(summaryData);
      
      // Notify parent of pending count for badge
      if (onPendingCountChange) {
        onPendingCountChange(summaryData.pending?.count || 0);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [filter, onPendingCountChange, API_BASE_URL]);

  useEffect(() => {
    fetchPendingPayments();
  }, [fetchPendingPayments]);

  const handleApprove = async (id) => {
    try {
      setProcessing(prev => ({ ...prev, [id]: 'approving' }));
      const token = localStorage.getItem('token');
      
      const response = await fetch(`${API_BASE_URL}/PendingPayments/${id}/approve`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to approve');
      }

      // Refresh the list
      await fetchPendingPayments();
    } catch (err) {
      alert(err.message);
    } finally {
      setProcessing(prev => ({ ...prev, [id]: null }));
    }
  };

  const handleReject = async (id) => {
    try {
      setProcessing(prev => ({ ...prev, [id]: 'rejecting' }));
      const token = localStorage.getItem('token');
      
      const response = await fetch(`${API_BASE_URL}/PendingPayments/${id}/reject`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ reason: rejectReason })
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to reject');
      }

      setShowRejectModal(null);
      setRejectReason('');
      await fetchPendingPayments();
    } catch (err) {
      alert(err.message);
    } finally {
      setProcessing(prev => ({ ...prev, [id]: null }));
    }
  };

  const handleBulkApprove = async () => {
    if (selectedIds.size === 0) return;
    
    if (!confirm(`Approve ${selectedIds.size} payment${selectedIds.size > 1 ? 's' : ''}?`)) {
      return;
    }

    try {
      setProcessing(prev => ({ ...prev, bulk: true }));
      const token = localStorage.getItem('token');
      
      const response = await fetch(`${API_BASE_URL}/PendingPayments/bulk-approve`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ ids: Array.from(selectedIds) })
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || 'Failed to bulk approve');
      }

      alert(`Successfully approved ${data.approved} payment${data.approved > 1 ? 's' : ''}`);
      setSelectedIds(new Set());
      await fetchPendingPayments();
    } catch (err) {
      alert(err.message);
    } finally {
      setProcessing(prev => ({ ...prev, bulk: false }));
    }
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === pendingPayments.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(pendingPayments.map(p => p.id)));
    }
  };

  const toggleSelect = (id) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedIds(newSelected);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  const getPaymentMethodIcon = (method) => {
    switch (method?.toLowerCase()) {
      case 'venmo':
        return <span className="w-6 h-6 bg-blue-500 rounded text-white text-xs flex items-center justify-center font-bold">V</span>;
      case 'zelle':
        return <span className="w-6 h-6 bg-purple-600 rounded text-white text-xs flex items-center justify-center font-bold">Z</span>;
      case 'cashapp':
        return <span className="w-6 h-6 bg-green-500 rounded text-white text-xs flex items-center justify-center font-bold">$</span>;
      case 'paypal':
        return <span className="w-6 h-6 bg-blue-700 rounded text-white text-xs flex items-center justify-center font-bold">P</span>;
      case 'cash':
        return <Banknote size={20} className="text-green-600" />;
      case 'check':
        return <Check size={20} className="text-indigo-600" />;
      default:
        return <CreditCard size={20} className="text-gray-500" />;
    }
  };

  // Mobile: Simple icon, Desktop: Full badge
  const getStatusIndicator = (status) => {
    switch (status) {
      case 'Pending':
        return (
          <>
            {/* Mobile: Just icon */}
            <span className="sm:hidden">
              <Clock size={16} className="text-yellow-600" />
            </span>
            {/* Desktop: Full badge */}
            <span className="hidden sm:flex px-2 py-1 text-xs rounded-full items-center gap-1" style={{background: 'linear-gradient(135deg, #422006, #78350f)', color: '#fcd34d'}}>
              <Clock size={12} /> Pending
            </span>
          </>
        );
      case 'Approved':
        return (
          <>
            {/* Mobile: Just checkmark */}
            <span className="sm:hidden">
              <CheckCircle size={16} className="text-green-600" />
            </span>
            {/* Desktop: Full badge */}
            <span className="hidden sm:flex px-2 py-1 text-xs rounded-full items-center gap-1" style={{background: 'linear-gradient(135deg, #052e16, #14532d)', color: '#86efac'}}>
              <CheckCircle size={12} /> Approved
            </span>
          </>
        );
      case 'Rejected':
        return (
          <>
            {/* Mobile: Just X */}
            <span className="sm:hidden">
              <XCircle size={16} className="text-red-600" />
            </span>
            {/* Desktop: Full badge */}
            <span className="hidden sm:flex px-2 py-1 text-xs rounded-full items-center gap-1" style={{background: 'linear-gradient(135deg, #450a0a, #7f1d1d)', color: '#fca5a5'}}>
              <XCircle size={12} /> Rejected
            </span>
          </>
        );
      default:
        return null;
    }
  };

  if (loading && !summary) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="w-6 h-6 animate-spin text-blue-600" />
        <span className="ml-2 text-gray-600">Loading reconciliation data...</span>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Summary Section */}
      {summary && (
        <>
          {/* Mobile: Compact horizontal bar */}
          <div className="md:hidden bg-white border border-gray-200 rounded-xl p-3">
            <div className="flex items-center justify-between divide-x divide-gray-200">
              <div className="flex-1 text-center px-2">
                <div className="flex items-center justify-center gap-1">
                  <Clock size={14} className="text-yellow-600" />
                  <span className="font-bold text-yellow-900">{summary.pending?.count || 0}</span>
                </div>
                <p className="text-xs text-gray-500">Pending</p>
              </div>
              <div className="flex-1 text-center px-2">
                <div className="flex items-center justify-center gap-1">
                  <FileText size={14} className="text-red-600" />
                  <span className="font-bold text-red-900">{summary.unpaidInvoices?.count || 0}</span>
                </div>
                <p className="text-xs text-gray-500">Unpaid</p>
              </div>
              <div className="flex-1 text-center px-2">
                <div className="flex items-center justify-center gap-1">
                  <CheckCircle size={14} className="text-green-600" />
                  <span className="font-bold text-green-900">{summary.approvedThisWeek?.count || 0}</span>
                </div>
                <p className="text-xs text-gray-500">Approved</p>
              </div>
            </div>
          </div>

          {/* Desktop: Full cards */}
          <div className="hidden md:grid grid-cols-3 gap-4">
            <div className="rounded-xl p-4" style={{background: 'linear-gradient(135deg, #422006, #78350f)'}}>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-amber-300 font-medium">Awaiting Review</p>
                  <p className="text-2xl font-bold text-amber-100">{summary.pending?.count || 0}</p>
                  <p className="text-sm text-amber-400">{formatCurrency(summary.pending?.total || 0)}</p>
                </div>
                <div className="w-12 h-12 bg-amber-900/50 rounded-full flex items-center justify-center">
                  <Clock className="w-6 h-6 text-amber-300" />
                </div>
              </div>
            </div>

            <div className="rounded-xl p-4" style={{background: 'linear-gradient(135deg, #450a0a, #7f1d1d)'}}>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-red-300 font-medium">Unpaid Invoices</p>
                  <p className="text-2xl font-bold text-red-100">{summary.unpaidInvoices?.count || 0}</p>
                  <p className="text-sm text-red-400">{formatCurrency(summary.unpaidInvoices?.total || 0)}</p>
                </div>
                <div className="w-12 h-12 bg-red-900/50 rounded-full flex items-center justify-center">
                  <FileText className="w-6 h-6 text-red-300" />
                </div>
              </div>
            </div>

            <div className="rounded-xl p-4" style={{background: 'linear-gradient(135deg, #052e16, #14532d)'}}>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-green-300 font-medium">Approved This Week</p>
                  <p className="text-2xl font-bold text-green-100">{summary.approvedThisWeek?.count || 0}</p>
                  <p className="text-sm text-green-400">{formatCurrency(summary.approvedThisWeek?.total || 0)}</p>
                </div>
                <div className="w-12 h-12 bg-green-900/50 rounded-full flex items-center justify-center">
                  <CheckCircle className="w-6 h-6 text-green-300" />
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Filter & Actions Bar */}
      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
        <div className="flex items-center gap-2">
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
          >
            <option value="Pending">Pending Review</option>
            <option value="Approved">Approved</option>
            <option value="Rejected">Rejected</option>
          </select>
          
          <button
            onClick={fetchPendingPayments}
            className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg"
            title="Refresh"
          >
            <RefreshCw size={18} className={loading ? 'animate-spin' : ''} />
          </button>
        </div>

        {filter === 'Pending' && selectedIds.size > 0 && (
          <button
            onClick={handleBulkApprove}
            disabled={processing.bulk}
            className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm font-medium flex items-center gap-2 disabled:opacity-50"
          >
            <CheckCircle size={16} />
            Approve Selected ({selectedIds.size})
          </button>
        )}
      </div>

      {/* Payments List */}
      {error ? (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
          {error}
        </div>
      ) : pendingPayments.length === 0 ? (
        <div className="bg-gray-50 border border-gray-200 rounded-xl p-8 text-center">
          <div className="w-16 h-16 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-4">
            {filter === 'Pending' ? <Clock className="w-8 h-8 text-gray-400" /> : <CheckCircle className="w-8 h-8 text-gray-400" />}
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-1">
            {filter === 'Pending' ? 'No Pending Payments' : `No ${filter} Payments`}
          </h3>
          <p className="text-gray-500 text-sm">
            {filter === 'Pending' 
              ? 'When customers confirm payments via email, they\'ll appear here for your approval.'
              : `You don't have any ${filter.toLowerCase()} payment confirmations yet.`}
          </p>
        </div>
      ) : (
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          {/* Table Header */}
          <div className="bg-gray-50 border-b border-gray-200 px-3 sm:px-4 py-3">
            <div className="flex items-center gap-2 sm:gap-4">
              {filter === 'Pending' && (
                <input
                  type="checkbox"
                  checked={selectedIds.size === pendingPayments.length && pendingPayments.length > 0}
                  onChange={toggleSelectAll}
                  className="w-4 h-4 rounded border-gray-300"
                />
              )}
              <span className="text-xs font-medium text-gray-500 uppercase tracking-wider flex-1">
                Customer / Invoice
              </span>
              <span className="text-xs font-medium text-gray-500 uppercase tracking-wider w-20 sm:w-28 text-right">
                Amount
              </span>
              {filter === 'Pending' ? (
                <span className="text-xs font-medium text-gray-500 uppercase tracking-wider w-16 sm:w-36 text-center hidden sm:block">
                  Actions
                </span>
              ) : (
                <span className="text-xs font-medium text-gray-500 uppercase tracking-wider w-8 sm:w-24 text-center">
                  {/* Status header - narrower on mobile */}
                </span>
              )}
            </div>
          </div>

          {/* Payment Rows */}
          <div className="divide-y divide-gray-100">
            {pendingPayments.map((payment) => (
              <div key={payment.id} className="hover:bg-gray-50">
                {/* Main Row */}
                <div className="px-3 sm:px-4 py-3">
                  <div className="flex items-center gap-2 sm:gap-4">
                    {filter === 'Pending' && (
                      <input
                        type="checkbox"
                        checked={selectedIds.has(payment.id)}
                        onChange={() => toggleSelect(payment.id)}
                        className="w-4 h-4 rounded border-gray-300"
                      />
                    )}
                    
                    {/* Customer & Invoice */}
                    <div 
                      className="flex-1 min-w-0 cursor-pointer"
                      onClick={() => setExpandedId(expandedId === payment.id ? null : payment.id)}
                    >
                      <div className="flex items-center gap-1 sm:gap-2">
                        {/* Payment method icon - mobile only, inline with name */}
                        <span className="sm:hidden flex-shrink-0">
                          {getPaymentMethodIcon(payment.paymentMethod)}
                        </span>
                        <p className="font-medium text-gray-900 truncate text-sm sm:text-base">
                          {payment.customerName || 'Unknown Customer'}
                        </p>
                        {expandedId === payment.id ? <ChevronUp size={14} className="flex-shrink-0" /> : <ChevronDown size={14} className="flex-shrink-0" />}
                      </div>
                      <p className="text-xs sm:text-sm text-gray-500 truncate">
                        Invoice #{payment.invoiceNumber}
                        {payment.reference && (
                          <span className="hidden sm:inline ml-2 text-gray-400">• Ref: {payment.reference}</span>
                        )}
                      </p>
                    </div>

                    {/* Payment Method - Desktop only */}
                    <div className="w-24 justify-center hidden md:flex">
                      <div className="flex items-center gap-2">
                        {getPaymentMethodIcon(payment.paymentMethod)}
                        <span className="text-sm text-gray-600">{payment.paymentMethod}</span>
                      </div>
                    </div>

                    {/* Amount */}
                    <div className="w-20 sm:w-28 text-right flex-shrink-0">
                      <p className="font-semibold text-gray-900 text-sm sm:text-base">{formatCurrency(payment.amount)}</p>
                      <p className="text-xs text-gray-500 hidden sm:block">
                        of {formatCurrency(payment.invoiceAmountDue)} due
                      </p>
                    </div>

                    {/* Actions - Pending */}
                    {filter === 'Pending' && (
                      <div className="w-auto sm:w-36 flex items-center justify-end sm:justify-center gap-1 sm:gap-2 flex-shrink-0">
                        <button
                          onClick={(e) => { e.stopPropagation(); handleApprove(payment.id); }}
                          disabled={processing[payment.id]}
                          className="p-2 sm:px-3 sm:py-1.5 bg-green-600 hover:bg-green-700 text-white text-sm rounded-lg disabled:opacity-50 flex items-center gap-1"
                        >
                          {processing[payment.id] === 'approving' ? (
                            <RefreshCw size={14} className="animate-spin" />
                          ) : (
                            <CheckCircle size={14} />
                          )}
                          <span className="hidden sm:inline">Approve</span>
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); setShowRejectModal(payment.id); }}
                          disabled={processing[payment.id]}
                          className="p-2 sm:px-3 sm:py-1.5 bg-white hover:bg-gray-100 text-gray-700 border border-gray-300 text-sm rounded-lg disabled:opacity-50"
                        >
                          <XCircle size={14} />
                        </button>
                      </div>
                    )}

                    {/* Status indicator - Non-Pending */}
                    {filter !== 'Pending' && (
                      <div className="w-8 sm:w-24 flex justify-center flex-shrink-0">
                        {getStatusIndicator(payment.status)}
                      </div>
                    )}
                  </div>
                </div>

                {/* Expanded Details */}
                {expandedId === payment.id && (
                  <div className="px-3 sm:px-4 pb-4 bg-gray-50 border-t border-gray-100">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 pt-3">
                      <div className="space-y-2">
                        <div className="flex items-center gap-2 text-sm">
                          <User size={14} className="text-gray-400" />
                          <span className="text-gray-600">Customer:</span>
                          <span className="font-medium">{payment.customerName || 'N/A'}</span>
                        </div>
                        {payment.customerEmail && (
                          <div className="flex items-center gap-2 text-sm">
                            <Mail size={14} className="text-gray-400" />
                            <span className="text-gray-600">Email:</span>
                            <a href={`mailto:${payment.customerEmail}`} className="text-blue-600 hover:underline truncate">
                              {payment.customerEmail}
                            </a>
                          </div>
                        )}
                        <div className="flex items-center gap-2 text-sm">
                          <FileText size={14} className="text-gray-400" />
                          <span className="text-gray-600">Invoice Total:</span>
                          <span className="font-medium">{formatCurrency(payment.invoiceAmount)}</span>
                        </div>
                        {/* Mobile: Show payment method in expanded */}
                        <div className="flex items-center gap-2 text-sm sm:hidden">
                          <DollarSign size={14} className="text-gray-400" />
                          <span className="text-gray-600">Method:</span>
                          <span className="font-medium">{payment.paymentMethod}</span>
                        </div>
                      </div>
                      
                      <div className="space-y-2">
                        <div className="flex items-center gap-2 text-sm">
                          <Calendar size={14} className="text-gray-400" />
                          <span className="text-gray-600">Submitted:</span>
                          <span className="font-medium">{formatDate(payment.submittedAt)}</span>
                        </div>
                        {payment.reference && (
                          <div className="flex items-center gap-2 text-sm">
                            <CreditCard size={14} className="text-gray-400" />
                            <span className="text-gray-600">Reference:</span>
                            <span className="font-medium">{payment.reference}</span>
                          </div>
                        )}
                        {payment.customerNote && (
                          <div className="flex items-start gap-2 text-sm">
                            <AlertCircle size={14} className="text-gray-400 mt-0.5" />
                            <span className="text-gray-600">Note:</span>
                            <span className="font-medium text-gray-800">{payment.customerNote}</span>
                          </div>
                        )}
                      </div>
                    </div>

                    {payment.status === 'Rejected' && payment.rejectionReason && (
                      <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                        <p className="text-sm text-red-700">
                          <strong>Rejection Reason:</strong> {payment.rejectionReason}
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Reject Modal */}
      {showRejectModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Reject Payment Confirmation</h3>
            <p className="text-sm text-gray-600 mb-4">
              Optionally provide a reason for rejecting this payment confirmation.
            </p>
            
            <textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="e.g., Payment not received, incorrect amount..."
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
            />

            <div className="flex gap-3 mt-4">
              <button
                onClick={() => {
                  setShowRejectModal(null);
                  setRejectReason('');
                }}
                className="flex-1 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg font-medium"
              >
                Cancel
              </button>
              <button
                onClick={() => handleReject(showRejectModal)}
                disabled={processing[showRejectModal]}
                className="flex-1 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {processing[showRejectModal] === 'rejecting' ? (
                  <RefreshCw size={16} className="animate-spin" />
                ) : (
                  <XCircle size={16} />
                )}
                Reject
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PaymentReconciliation;